import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { calculateOffset } from 'utils/helpers';

import { apiQuery } from '../../query';

import {
  FiservRetrieval,
  FiservCaseDetail,
  FiservReturnReport,
  FiservRetrievalDetail,
  GetFiservTokenResponse,
  FiservRetrievalsRequest,
  CreateFiservTokenRequest,
  FiservRetrievalsResponse,
  UpdateFiservTokenRequest,
  CreateFiservTokenResponse,
  UpdateFiservRetrievalRequest,
  FiservRetrievalSummaryResponse,
} from './types';

const tagTypes = ['Fiserv'];

export const fiservApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'fiservApi',
  endpoints: (build) => ({
    getToken: build.query<GetFiservTokenResponse, void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.GET_FISERV_TOKEN,
      }),
    }),

    returnsReport: build.query<FiservReturnReport[], void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.GET_FISERV_RETURNS_REPORT,
      }),
    }),

    deleteToken: build.mutation<CreateFiservTokenResponse, void>({
      invalidatesTags: tagTypes,
      query: () => ({
        method: 'delete',
        url: URL.GET_FISERV_TOKEN,
      }),
    }),

    retrievalsDetail: build.query<FiservRetrievalDetail[], void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.GET_FISERV_RETRIEVALS_DETAIL,
      }),
    }),

    retrievalsSummary: build.query<FiservRetrievalSummaryResponse, void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.GET_FISERV_RETRIEVALS_SUMMARY,
      }),
    }),

    retrievalDetails: build.query<FiservRetrieval, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: generatePath(URL.GET_FISERV_RETRIEVAL, { id }),
      }),
    }),

    caseDetails: build.query<FiservCaseDetail[], string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: generatePath(URL.GET_FISERV_CASE_DETAILS, { id }),
      }),
    }),

    addToken: build.mutation<
      CreateFiservTokenResponse,
      CreateFiservTokenRequest
    >({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.GET_FISERV_TOKEN,
      }),
    }),

    updateToken: build.mutation<
      CreateFiservTokenResponse,
      UpdateFiservTokenRequest
    >({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'patch',
        url: URL.GET_FISERV_TOKEN,
      }),
    }),

    updateRetrieval: build.mutation<
      FiservRetrieval,
      UpdateFiservRetrievalRequest
    >({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: generatePath(URL.UPDATE_FISERV_RETRIEVAL, { id }),
      }),
    }),

    retrievals: build.query<FiservRetrievalsResponse, FiservRetrievalsRequest>({
      providesTags: tagTypes,
      query: (params) => {
        const { page = 1, limit = 20, ...otherParams } = params || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };
        return {
          method: 'get',
          url: addParamsToUrl(URL.GET_FISERV_RETRIEVALS, searchParams),
        };
      },
    }),
  }),
});

export const {
  useGetTokenQuery,
  useRetrievalsQuery,
  useAddTokenMutation,
  useCaseDetailsQuery,
  useReturnsReportQuery,
  useDeleteTokenMutation,
  useUpdateTokenMutation,
  useRetrievalsDetailQuery,
  useRetrievalDetailsQuery,
  useRetrievalsSummaryQuery,
  useUpdateRetrievalMutation,
  useLazyRetrievalDetailsQuery,
} = fiservApi;
